'use client'

import { useAllKalshiPositions } from '@/hooks/kalshi/useKalshiPositions'
import { useSolanaUsdcBalance } from '@/hooks/kalshi/useSolanaBalance'
import usePolygonBalances from '@/hooks/polymarket/usePolygonBalances'
import useUserPositions from '@/hooks/polymarket/useUserPositions'
import { cn } from '@/lib/utils'
import { useTrading } from '@/providers/TradingProvider'
import { usePendingBalanceStore } from '@/stores/pending-balance-store'
import { useUser } from '@account-kit/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useRef } from 'react'

export function BalanceDisplay() {
	const t = useTranslations('header')
	const user = useUser()
	const { safeAddress } = useTrading()

	// Polygon (Polymarket) balances
	const { formattedUsdcBalance: polygonUsdc, isLoading: polygonLoading } =
		usePolygonBalances(safeAddress)
	const { data: polyPositions = [], isLoading: polyPosLoading } = useUserPositions(safeAddress)

	// Solana (Kalshi) balances
	const { data: solanaBalance, isLoading: solanaLoading } = useSolanaUsdcBalance()
	const { data: kalshiPositions = [], isLoading: kalshiPosLoading } = useAllKalshiPositions()

	// Pending balance changes (optimistic updates) - tracked separately
	const {
		pendingCashChange,
		pendingPositionChange,
		hasPendingCash,
		hasPendingPositions,
		clearPendingCash,
		clearPendingPositions,
	} = usePendingBalanceStore()

	// Progressive loading: track each source independently
	const polygonCashReady = Boolean(!polygonLoading && polygonUsdc !== undefined)
	const solanaCashReady = Boolean(!solanaLoading && solanaBalance !== undefined)
	const polyPositionsReady = !polyPosLoading
	const kalshiPositionsReady = !kalshiPosLoading

	// Show loading only if NO data source has loaded yet
	const hasAnyCashData = polygonCashReady || solanaCashReady
	const hasAnyPositionData = polyPositionsReady || kalshiPositionsReady

	// Combined cash balance - only include ready sources
	const polygonCash = polygonCashReady ? Number.parseFloat(polygonUsdc) || 0 : 0
	const solanaCash = solanaCashReady ? Number.parseFloat(solanaBalance?.formatted || '0') || 0 : 0
	const realCash = polygonCash + solanaCash

	// Combined position value - only include ready sources
	const polyPositionValue = polyPositionsReady
		? polyPositions.reduce((sum, p) => sum + p.currentValue, 0)
		: 0
	const kalshiPositionValue = kalshiPositionsReady
		? kalshiPositions.reduce((sum, p) => sum + p.balance * (p.currentPrice || 0), 0)
		: 0
	const realPositionValue = polyPositionValue + kalshiPositionValue

	// Track previous values to detect when API updates
	const prevCashRef = useRef(realCash)
	const prevPositionsRef = useRef(realPositionValue)

	useEffect(() => {
		// Clear pending cash when real cash changes
		if (hasPendingCash && prevCashRef.current !== realCash) {
			clearPendingCash()
		}
		prevCashRef.current = realCash
	}, [realCash, hasPendingCash, clearPendingCash])

	useEffect(() => {
		// Clear pending positions when real positions change
		if (hasPendingPositions && prevPositionsRef.current !== realPositionValue) {
			clearPendingPositions()
		}
		prevPositionsRef.current = realPositionValue
	}, [realPositionValue, hasPendingPositions, clearPendingPositions])

	// Show pending optimistically, but only if API hasn't caught up yet
	const totalCash = realCash + (hasPendingCash ? pendingCashChange : 0)
	const totalPositionValue = realPositionValue + (hasPendingPositions ? pendingPositionChange : 0)

	// Total P&L (only Polymarket tracks this currently)
	const totalPnL = polyPositions.reduce((sum, p) => sum + p.cashPnl, 0)

	if (!(user && safeAddress)) {
		return null
	}

	// Show loading indicator with subtle animation for sources still loading
	const cashLoading = Boolean(!hasAnyCashData && (polygonLoading || solanaLoading))
	const positionsLoading = Boolean(!hasAnyPositionData && (polyPosLoading || kalshiPosLoading))

	return (
		<Link
			href="/portfolio"
			className="hidden h-[40px] cursor-pointer items-center gap-[10px] rounded-[16px] border border-[#2a2a2a] bg-[#101215] px-[16px] py-[8px] transition-colors lg:flex"
		>
			{/* Cash section */}
			<div className="flex items-center gap-[6px] text-[14px]">
				<span className="font-normal text-white/75">{t('cash')}</span>
				{cashLoading ? (
					<span className="font-medium text-white">---.--</span>
				) : (
					<span
						className={cn(
							'font-medium text-white',
							hasPendingCash && 'animate-pulse text-emerald-400',
							Boolean(!(polygonCashReady && solanaCashReady)) && 'opacity-75'
						)}
					>
						${totalCash.toFixed(2)}
					</span>
				)}
			</div>

			{/* Divider */}
			<div className="h-full w-px rounded-[4px] bg-[#2a2a2a]" />

			{/* Positions section */}
			<div className="flex items-center gap-[6px]">
				<span className="font-normal text-[14px] text-white/75">{t('positions')}</span>
				{positionsLoading ? (
					<span className="font-medium text-[14px] text-white">---.--</span>
				) : (
					<>
						<span
							className={cn(
								'font-medium text-[14px] text-white',
								hasPendingPositions && 'animate-pulse',
								Boolean(!(polyPositionsReady && kalshiPositionsReady)) && 'opacity-75'
							)}
						>
							${totalPositionValue.toFixed(2)}
						</span>
						<span
							className={cn(
								'font-medium text-[12px]',
								totalPnL >= 0 ? 'text-brand-green' : 'text-[#ef4444]',
								Boolean(!(polyPositionsReady && kalshiPositionsReady)) && 'opacity-75'
							)}
						>
							{totalPnL >= 0 ? '+' : ''}
							{totalPnL.toFixed(2)}%
						</span>
					</>
				)}
			</div>
		</Link>
	)
}
